import React, { useEffect, useState } from "react";
import api, { apiErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Plus, Trash2, Info, Bell, ListChecks } from "lucide-react";
import { toast } from "sonner";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const DAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
const HOURS = Array.from({ length: 24 }, (_, i) => i);
const TIMEZONES = ["Australia/Sydney", "Australia/Melbourne", "Australia/Brisbane", "Australia/Perth", "Pacific/Auckland", "UTC", "America/New_York", "America/Los_Angeles", "Europe/London"];
const hourLabel = (h) => `${((h + 11) % 12) + 1}:00 ${h < 12 ? "AM" : "PM"}`;

export default function Settings() {
  const [groups, setGroups] = useState([]);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [savingCl, setSavingCl] = useState(false);
  const [savingS, setSavingS] = useState(false);
  const [active, setActive] = useState(0);

  const load = async () => {
    setLoading(true);
    const [c, s] = await Promise.all([api.get("/checklist-templates"), api.get("/settings/notifications")]);
    setGroups(c.data.map((g) => ({ key: g.key, label: g.label, items: g.items.map((v) => ({ id: crypto.randomUUID(), value: v })) })));
    setSettings(s.data);
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const g = groups[active];
  const setItem = (i, v) => setGroups((arr) => arr.map((x, idx) => idx === active ? { ...x, items: x.items.map((it, j) => j === i ? { ...it, value: v } : it) } : x));
  const addItem = () => setGroups((arr) => arr.map((x, idx) => idx === active ? { ...x, items: [...x.items, { id: crypto.randomUUID(), value: "" }] } : x));
  const removeItem = (i) => setGroups((arr) => arr.map((x, idx) => idx === active ? { ...x, items: x.items.filter((_, j) => j !== i) } : x));
  const setLabel = (v) => setGroups((arr) => arr.map((x, idx) => idx === active ? { ...x, label: v } : x));

  const saveChecklists = async () => {
    setSavingCl(true);
    try {
      const res = await api.put("/checklist-templates", {
        groups: groups.map((x) => ({ key: x.key, label: x.label, items: x.items.map((i) => i.value).filter((v) => v.trim()) })),
      });
      setGroups(res.data.groups.map((x) => ({ key: x.key, label: x.label, items: x.items.map((v) => ({ id: crypto.randomUUID(), value: v })) })));
      toast.success("Lodgement checklists saved");
    } catch (e) { toast.error(apiErr(e, "Failed to save")); }
    finally { setSavingCl(false); }
  };

  const saveSettings = async () => {
    setSavingS(true);
    try {
      const res = await api.put("/settings/notifications", { weekly: settings.weekly, daily: settings.daily, push_digest: settings.push_digest });
      setSettings(res.data);
      toast.success("Notification schedule saved");
    } catch (e) { toast.error("Failed to save schedule"); }
    finally { setSavingS(false); }
  };

  if (loading || !settings) return <div className="p-10"><div className="h-8 w-8 border-2 border-zinc-900 border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div className="p-6 lg:p-10 max-w-[820px]">
      <header className="mb-8">
        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-zinc-500 mb-1">Admin</p>
        <h1 className="font-heading text-4xl font-black tracking-tighter">Settings</h1>
      </header>

      {/* Notification schedule */}
      <section className="mb-12">
        <div className="flex items-center gap-2 mb-4">
          <Bell className="h-4 w-4 text-zinc-500" />
          <h2 className="font-heading text-xl font-bold">Notification Schedule</h2>
        </div>

        <div className="bg-white border border-zinc-200 divide-y divide-zinc-100">
          <div className="p-5">
            <div className="flex items-center justify-between mb-4">
              <div><p className="font-semibold text-sm">Weekly Digest</p><p className="text-xs text-zinc-500">Summary of overdue & this-week tasks to admins</p></div>
              <Switch data-testid="weekly-enabled" checked={settings.weekly.enabled} onCheckedChange={(v) => setSettings({ ...settings, weekly: { ...settings.weekly, enabled: v } })} />
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <label className="text-[10px] font-semibold uppercase tracking-wider text-zinc-500">Day</label>
                <Select value={String(settings.weekly.day)} onValueChange={(v) => setSettings({ ...settings, weekly: { ...settings.weekly, day: Number(v) } })}>
                  <SelectTrigger className="rounded-sm mt-1" data-testid="weekly-day"><SelectValue /></SelectTrigger>
                  <SelectContent>{DAYS.map((d, i) => <SelectItem key={d} value={String(i)}>{d}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-[10px] font-semibold uppercase tracking-wider text-zinc-500">Time</label>
                <Select value={String(settings.weekly.hour)} onValueChange={(v) => setSettings({ ...settings, weekly: { ...settings.weekly, hour: Number(v) } })}>
                  <SelectTrigger className="rounded-sm mt-1" data-testid="weekly-hour"><SelectValue /></SelectTrigger>
                  <SelectContent>{HOURS.map((h) => <SelectItem key={h} value={String(h)}>{hourLabel(h)}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-[10px] font-semibold uppercase tracking-wider text-zinc-500">Timezone</label>
                <Select value={settings.weekly.timezone} onValueChange={(v) => setSettings({ ...settings, weekly: { ...settings.weekly, timezone: v } })}>
                  <SelectTrigger className="rounded-sm mt-1" data-testid="weekly-tz"><SelectValue /></SelectTrigger>
                  <SelectContent>{TIMEZONES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
          </div>

          <div className="p-5">
            <div className="flex items-center justify-between mb-4">
              <div><p className="font-semibold text-sm">Daily Due-Soon Nudge</p><p className="text-xs text-zinc-500">Emails each teammate their tasks due tomorrow</p></div>
              <Switch data-testid="daily-enabled" checked={settings.daily.enabled} onCheckedChange={(v) => setSettings({ ...settings, daily: { ...settings.daily, enabled: v } })} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-[10px] font-semibold uppercase tracking-wider text-zinc-500">Time</label>
                <Select value={String(settings.daily.hour)} onValueChange={(v) => setSettings({ ...settings, daily: { ...settings.daily, hour: Number(v) } })}>
                  <SelectTrigger className="rounded-sm mt-1" data-testid="daily-hour"><SelectValue /></SelectTrigger>
                  <SelectContent>{HOURS.map((h) => <SelectItem key={h} value={String(h)}>{hourLabel(h)}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-[10px] font-semibold uppercase tracking-wider text-zinc-500">Timezone</label>
                <Select value={settings.daily.timezone} onValueChange={(v) => setSettings({ ...settings, daily: { ...settings.daily, timezone: v } })}>
                  <SelectTrigger className="rounded-sm mt-1" data-testid="daily-tz"><SelectValue /></SelectTrigger>
                  <SelectContent>{TIMEZONES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
          </div>

          <div className="p-5">
            <div className="flex items-center justify-between mb-4">
              <div><p className="font-semibold text-sm">Daily Push Digest</p><p className="text-xs text-zinc-500">One condensed push per teammate summarizing overdue, due-this-week & staffing counts</p></div>
              <Switch data-testid="push-digest-enabled" checked={settings.push_digest?.enabled ?? true} onCheckedChange={(v) => setSettings({ ...settings, push_digest: { ...(settings.push_digest || {}), enabled: v } })} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-[10px] font-semibold uppercase tracking-wider text-zinc-500">Time</label>
                <Select value={String(settings.push_digest?.hour ?? 8)} onValueChange={(v) => setSettings({ ...settings, push_digest: { ...(settings.push_digest || {}), hour: Number(v) } })}>
                  <SelectTrigger className="rounded-sm mt-1" data-testid="push-digest-hour"><SelectValue /></SelectTrigger>
                  <SelectContent>{HOURS.map((h) => <SelectItem key={h} value={String(h)}>{hourLabel(h)}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-[10px] font-semibold uppercase tracking-wider text-zinc-500">Timezone</label>
                <Select value={settings.push_digest?.timezone ?? "Australia/Sydney"} onValueChange={(v) => setSettings({ ...settings, push_digest: { ...(settings.push_digest || {}), timezone: v } })}>
                  <SelectTrigger className="rounded-sm mt-1" data-testid="push-digest-tz"><SelectValue /></SelectTrigger>
                  <SelectContent>{TIMEZONES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="mt-4 flex items-center gap-3">
              <Button variant="outline" size="sm" className="rounded-sm" data-testid="push-digest-test-btn"
                onClick={async () => { try { const { data } = await api.post("/push/digest-test"); toast.success(`Preview sent to your devices: ${data.body}`); } catch (e) { toast.error("Couldn't send — enable push notifications on this device first."); } }}>
                Send test digest now
              </Button>
              <span className="text-xs text-zinc-500">Sends a preview of your digest to this device&apos;s push notifications.</span>
            </div>
          </div>
        </div>
        <div className="mt-4 flex justify-end">
          <Button onClick={saveSettings} disabled={savingS} className="bg-zinc-900 hover:bg-zinc-800 rounded-sm" data-testid="save-settings">
            {savingS ? "Saving…" : "Save Schedule"}
          </Button>
        </div>
      </section>

      {/* Lodgement checklists */}
      <section>
        <div className="flex items-center gap-2 mb-4">
          <ListChecks className="h-4 w-4 text-zinc-500" />
          <h2 className="font-heading text-xl font-bold">Lodgement Checklists</h2>
        </div>
        <div className="flex items-start gap-2 bg-blue-50 border border-blue-200 text-blue-800 text-sm p-3 rounded-sm mb-4">
          <Info className="h-4 w-4 mt-0.5 shrink-0" />
          <p>Edit the one-click checklist templates used inside tasks. Changes apply to future &ldquo;apply&rdquo; actions; already-applied checklists on tasks are not altered.</p>
        </div>

        <div className="flex gap-2 mb-4 flex-wrap">
          {groups.map((grp, i) => (
            <button key={grp.key} onClick={() => setActive(i)} data-testid={`cl-tab-${grp.key}`}
              className={`px-4 h-9 rounded-sm text-sm font-semibold border transition-colors ${active === i ? "bg-zinc-900 text-white border-zinc-900" : "bg-white text-zinc-600 border-zinc-200 hover:bg-zinc-50"}`}>
              {grp.label} <span className="opacity-60">({grp.items.length})</span>
            </button>
          ))}
        </div>

        {g && (
          <div className="bg-white border border-zinc-200">
            <div className="p-4 border-b border-zinc-100">
              <label className="text-[10px] font-semibold uppercase tracking-wider text-zinc-500">Template Label</label>
              <Input value={g.label} onChange={(e) => setLabel(e.target.value)} className="rounded-sm mt-1" data-testid="cl-label-input" />
            </div>
            {g.items.map((it, i) => (
              <div key={it.id} className="flex items-center gap-2 px-4 py-2 border-b border-zinc-100 last:border-0" data-testid={`cl-item-${i}`}>
                <span className="text-xs text-zinc-400 w-6">{i + 1}</span>
                <Input value={it.value} onChange={(e) => setItem(i, e.target.value)} className="flex-1 rounded-sm border-transparent hover:border-zinc-200 focus:border-zinc-900" />
                <button onClick={() => removeItem(i)} className="text-zinc-400 hover:text-red-600" data-testid={`cl-remove-${i}`}><Trash2 className="h-4 w-4" /></button>
              </div>
            ))}
            <button onClick={addItem} className="flex items-center gap-2 px-4 py-3 text-sm text-zinc-500 hover:text-zinc-900 w-full" data-testid="cl-add"><Plus className="h-4 w-4" /> Add item</button>
          </div>
        )}
        <div className="mt-4 flex justify-end">
          <Button onClick={saveChecklists} disabled={savingCl} className="bg-zinc-900 hover:bg-zinc-800 rounded-sm" data-testid="save-checklists">
            {savingCl ? "Saving…" : "Save Checklists"}
          </Button>
        </div>
      </section>
    </div>
  );
}
